import type { PageObjectModel } from 'interfaces/project';
import { clamp } from 'utils/number';

export interface CropGeometryRect {
	height: number;
	width: number;
	x_axis: number;
	y_axis: number;
}

export interface CropRect {
	cropheight: number;
	cropwidth: number;
	cropx: number;
	cropy: number;
}

export interface PageObjectCropBounds {
	height: number;
	maxX: number;
	maxY: number;
	minX: number;
	minY: number;
	width: number;
}

export type PageObjectCropBoundsInput = Pick<
	PageObjectModel,
	'maxheight' | 'maxwidth'
> & Partial<Pick<
	PageObjectModel,
	'maxx' | 'maxy' | 'minx' | 'miny'
>>;

export interface SourceRect {
	height: number;
	width: number;
	x: number;
	y: number;
}

/**
 * Resolves a finite number with a fallback for null, undefined, and invalid values.
 *
 * @param value Candidate numeric value.
 * @param fallback Value to use when the candidate is not finite.
 * @returns Finite value safe for crop calculations.
 */
function getFiniteNumber(
	value: number | null | undefined,
	fallback: number,
): number {
	return (
		typeof value === 'number'
		&& Number.isFinite(value)
			? value
			: fallback
	);
}

/**
 * Converts visible source artwork into integer crop bounds that fully contain the artwork.
 *
 * @param sourceRect Visible artwork rectangle in source-image pixels.
 * @returns Integer crop bounds using floor for start edges and ceil for end edges.
 */
export function getCropBoundsFromVisibleSourceRect(sourceRect: SourceRect): PageObjectCropBounds {
	const minX = Math.floor(Math.max(
		sourceRect.x,
		0,
	));
	const minY = Math.floor(Math.max(
		sourceRect.y,
		0,
	));
	const maxX = Math.max(
		minX + 1,
		Math.ceil(sourceRect.x + sourceRect.width),
	);
	const maxY = Math.max(
		minY + 1,
		Math.ceil(sourceRect.y + sourceRect.height),
	);

	return {
		height: maxY - minY,
		maxX,
		maxY,
		minX,
		minY,
		width: maxX - minX,
	};
}

/**
 * Resolves the active croppable source bounds for a page object.
 *
 * @param objectModel {@link PageObjectCropBoundsInput} source dimensions and optional active crop boundaries to normalize.
 * @returns Normalized crop bounds, falling back to the full source dimensions when no min/max bounds exist.
 */
export function getPageObjectCropBounds(objectModel: PageObjectCropBoundsInput): PageObjectCropBounds {
	const sourceWidth = Math.max(
		Math.round(getFiniteNumber(
			objectModel.maxwidth,
			0,
		)),
		0,
	);
	const sourceHeight = Math.max(
		Math.round(getFiniteNumber(
			objectModel.maxheight,
			0,
		)),
		0,
	);
	const minX = Math.floor(clamp(
		getFiniteNumber(
			objectModel.minx,
			0,
		),
		0,
		sourceWidth,
	));
	const minY = Math.floor(clamp(
		getFiniteNumber(
			objectModel.miny,
			0,
		),
		0,
		sourceHeight,
	));
	let maxX = Math.ceil(clamp(
		getFiniteNumber(
			objectModel.maxx,
			sourceWidth,
		),
		minX,
		sourceWidth,
	));
	let maxY = Math.ceil(clamp(
		getFiniteNumber(
			objectModel.maxy,
			sourceHeight,
		),
		minY,
		sourceHeight,
	));

	if (
		maxX <= minX
		|| maxY <= minY
	) {
		/**
		 * Invalid persisted bounds should behave like legacy objects so old data
		 * or malformed sync payloads cannot produce a zero-sized crop source.
		 */
		maxX = sourceWidth;
		maxY = sourceHeight;

		return {
			height: sourceHeight,
			maxX,
			maxY,
			minX: 0,
			minY: 0,
			width: sourceWidth,
		};
	}

	return {
		height: maxY - minY,
		maxX,
		maxY,
		minX,
		minY,
		width: maxX - minX,
	};
}

/**
 * Normalizes page-object geometry emitted by crop operations to integer page pixels.
 *
 * @param geometryRect Raw page-object geometry values to normalize.
 * @returns Geometry values rounded to whole page pixels.
 */
export function normalizeCropGeometryRect(geometryRect: CropGeometryRect): CropGeometryRect {
	return {
		height: Math.round(geometryRect.height),
		width: Math.round(geometryRect.width),
		x_axis: Math.round(geometryRect.x_axis),
		y_axis: Math.round(geometryRect.y_axis),
	};
}

/**
 * Normalizes a crop rectangle to integer source pixels within active crop bounds.
 *
 * @param cropRect Raw crop rectangle to normalize.
 * @param cropBounds Active source bounds that constrain the crop rectangle.
 * @returns Crop rectangle with integer values that stays inside the provided bounds.
 */
export function normalizeCropRect(
	cropRect: CropRect,
	cropBounds: PageObjectCropBounds,
): CropRect {
	if (
		cropBounds.maxX <= cropBounds.minX
		|| cropBounds.maxY <= cropBounds.minY
	) {
		return {
			cropheight: 0,
			cropwidth: 0,
			cropx: cropBounds.minX,
			cropy: cropBounds.minY,
		};
	}

	const boundsWidth = Math.max(
		cropBounds.maxX - cropBounds.minX,
		1,
	);
	const boundsHeight = Math.max(
		cropBounds.maxY - cropBounds.minY,
		1,
	);
	const roundedLeft = Math.round(cropRect.cropx);
	const roundedTop = Math.round(cropRect.cropy);
	const roundedRight = Math.round(cropRect.cropx + cropRect.cropwidth);
	const roundedBottom = Math.round(cropRect.cropy + cropRect.cropheight);
	const cropwidth = Math.min(
		Math.max(
			roundedRight - roundedLeft,
			1,
		),
		boundsWidth,
	);
	const cropheight = Math.min(
		Math.max(
			roundedBottom - roundedTop,
			1,
		),
		boundsHeight,
	);
	const cropx = clamp(
		roundedLeft,
		cropBounds.minX,
		cropBounds.maxX - cropwidth,
	);
	const cropy = clamp(
		roundedTop,
		cropBounds.minY,
		cropBounds.maxY - cropheight,
	);

	return {
		cropheight,
		cropwidth,
		cropx,
		cropy,
	};
}

/**
 * Normalizes page-object crop fields against the object's active source bounds.
 *
 * @param objectModel {@link PageObjectModel} whose crop fields should be normalized.
 * @returns Page object with safe crop fields, or the original object when no change is needed.
 */
export function normalizePageObjectCrop(objectModel: PageObjectModel): PageObjectModel {
	const normalizedCrop = normalizeCropRect(
		objectModel,
		getPageObjectCropBounds(objectModel),
	);

	if (
		normalizedCrop.cropx === objectModel.cropx
		&& normalizedCrop.cropy === objectModel.cropy
		&& normalizedCrop.cropwidth === objectModel.cropwidth
		&& normalizedCrop.cropheight === objectModel.cropheight
	) {
		return objectModel;
	}

	return {
		...objectModel,
		...normalizedCrop,
	};
}
